// src/v2/pulse/spacePipeline.js
//
// SPACE-PIPELINE-S1 — pure bucketing/rollup logic for the Home page "Space
// pipeline" web part. Consumes the markets[].locations[] shape returned by
// GET /api/plan (marketsClient.getPlan()) — the same live data already used
// by the Plan page — so this view is read-only and adds no new endpoints.
//
// Buckets (mirrors server/routes/plan.js HEADROOM_* stage policy):
//   Coming online   — SPACE-PIPELINE-NET-CONSOLIDATION-S1: one row per
//                      MARKET (not per office), netting build-stage seat
//                      additions against same-market exits/closures over the
//                      same window — see buildComingOnlineRows() below.
//   Seats available — stage === "operational" and installedCapacity >= headcount
//                      (people-based surplus). Excludes offices with no headcount
//                      or no installedCapacity data.
//   Needs seats     — stage === "operational" and installedCapacity < headcount
//                      (people-based deficit). Metric = headcount − installedCapacity.
//                      Matches Space Fit report's Desk-fit column exactly.
//
// NOTE: seatGap (max_density − installed_seats) is retained only as the
// "headroom" (densification room) stat in the Seats available rows — it does
// NOT drive bucketing any more. This aligns both columns with the Space Fit
// report and eliminates false shortages caused by density-ceiling arithmetic.
//
// Seats available / Needs seats rows are one per qualifying LOCATION,
// labeled by its parent market's name (◆ marker when the location itself is
// the market's Hub office) — the portfolio has no per-office landing page,
// so "market" is the natural row identity here. Rollups sum the row metric;
// marketCount is the number of DISTINCT markets represented among the rows
// (a market can contribute more than one row to these two columns, e.g. two
// operational offices in the same market).

import { collectExitEvents, computeOfficeHorizonCapacity } from "../../features/plan/marketsService.js";
import { computePeopleDelta } from "../../utils/spaceFitVerdict.js";

const BUILD_STAGES = new Set(["build"]);
const EXIT_DISPOSITION = "exit";

export function onlineDateLabel(onlineDate) {
  const d = onlineDate instanceof Date ? onlineDate : parseHorizonDate(onlineDate);
  if (d == null) return "Online date TBD";
  return `Online ${d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })}`;
}

export function exitDateLabel(closeDate) {
  const d = closeDate instanceof Date ? closeDate : parseHorizonDate(closeDate);
  if (d == null) return "Exit date TBD";
  return `Exit ${d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })}`;
}

// SPACE-PIPELINE-NET-S1: parse a plan-API date (string or Date) to a UTC
// midnight Date, or null when absent/invalid. Shared by the market-horizon
// derivation and onlineDateLabel above.
function parseHorizonDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const s = typeof v === "string" ? v : String(v);
  const d = new Date(s.length > 10 ? s : `${s.slice(0, 10)}T00:00:00Z`);
  return isNaN(d.getTime()) ? null : d;
}

function formatSignedSeats(n) {
  return n >= 0 ? `+${n}` : `${n}`;
}

/**
 * Derive the canonical future capacity event for one office.
 *
 * The event is evaluated at the office's own online/effective date. It must
 * not use a later market horizon, because later schedule rows can represent a
 * separate phase. Schedule rows are authoritative when they are effective at
 * the candidate date; missing lifecycle dates never get fabricated.
 */
export function deriveComingOnlineEvent(office, today = new Date()) {
  const todayDate = parseHorizonDate(today);
  if (!todayDate) return null;

  const stage = office?.stage ?? "";
  if (stage !== "build" && stage !== "operational") return null;

  const capacityToday = computeOfficeHorizonCapacity(office, todayDate);
  const lifecycleDate = parseHorizonDate(office?.onlineDate);
  const scheduleDates = (Array.isArray(office?.futureScheduleRows)
    ? office.futureScheduleRows
    : [])
    .map((row) => parseHorizonDate(row?.effectiveDate))
    .filter((date) => date != null && date > todayDate);
  const candidateDates = [
    ...(stage === "build" && lifecycleDate && lifecycleDate > todayDate
      ? [lifecycleDate]
      : []),
    ...scheduleDates,
  ].sort((a, b) => a - b);

  const seen = new Set();
  for (const onlineDate of candidateDates) {
    const dateKey = onlineDate.getTime();
    if (seen.has(dateKey)) continue;
    seen.add(dateKey);

    const capacityAtOnlineDate = computeOfficeHorizonCapacity(office, onlineDate);
    const delta = capacityAtOnlineDate - capacityToday;
    if (delta <= 0) continue;

    return {
      officeId: office?.locationId ?? office?.id ?? null,
      officeName: office?.locationAlias
        ?? office?.location_alias
        ?? office?.name
        ?? office?.locationId
        ?? office?.id
        ?? null,
      delta,
      seatsAdded: delta,
      onlineDate,
    };
  }

  return null;
}

// SPACE-PIPELINE-NET-S1 / PULSE-COMING-ONLINE-INCLUDE-CAPACITY-SCHEDULE: a
// market's "coming online" horizon is the latest onlineDate (build stage),
// closeDate (exit disposition), OR the
// effective date of any future schedule row that represents a capacity
// *increase* at an existing operational office. The card has no horizon
// slider like What-If's 12m/24m tabs, so rather than pick an arbitrary fixed
// window we size the comparison window to whatever is actually scheduled; a
// market with nothing on file produces no "coming online" row at all.
function marketPipelineHorizon(locations, today) {
  let latest = null;
  for (const loc of locations ?? []) {
    if (BUILD_STAGES.has(loc.stage)) {
      const d = parseHorizonDate(loc.onlineDate);
      if (d != null && d > today && (latest == null || d > latest)) latest = d;
      for (const row of (Array.isArray(loc?.futureScheduleRows) ? loc.futureScheduleRows : [])) {
        const rd = parseHorizonDate(row?.effectiveDate);
        if (rd != null && rd > today && (latest == null || rd > latest)) latest = rd;
      }
    } else if (loc.stage === "operational" && loc.disposition === EXIT_DISPOSITION) {
      const d = parseHorizonDate(loc.closeDate);
      if (d != null && d > today && (latest == null || d > latest)) latest = d;
    } else if (loc.stage === "operational" && loc.disposition !== EXIT_DISPOSITION) {
      // PULSE-COMING-ONLINE-INCLUDE-CAPACITY-SCHEDULE: check for scheduled
      // capacity increases (futureScheduleRows where capacity at the row date
      // exceeds capacity today). Each qualifying row date extends the horizon.
      const rows = loc?.futureScheduleRows;
      if (!Array.isArray(rows) || rows.length === 0) continue;
      const capacityToday = computeOfficeHorizonCapacity(loc, today);
      for (const r of rows) {
        const rd = parseHorizonDate(r?.effectiveDate);
        if (rd == null || rd <= today) continue;
        const capacityAtRow = computeOfficeHorizonCapacity(loc, rd);
        if (capacityAtRow > capacityToday && (latest == null || rd > latest)) {
          latest = rd;
        }
      }
    }
  }
  return latest;
}

// PULSE-COMING-ONLINE-INCLUDE-CAPACITY-SCHEDULE: collect positive-delta
// capacity events for operational (non-build, non-exit, non-inactive) offices
// whose future schedule rows produce a net capacity increase vs. today within
// (today, horizon]. Each office emits at most one event — the earliest row
// within the window that triggers an increase — with delta =
// computeOfficeHorizonCapacity(loc, horizon) − computeOfficeHorizonCapacity(loc, today).
// Lives here (not marketsService.js) because it is Pulse-specific.
function collectScheduleIncreaseEvents(locations, today, horizon) {
  const events = [];
  for (const loc of (locations ?? [])) {
    const stage = loc?.stage ?? "";
    if (BUILD_STAGES.has(stage)) continue;
    if (stage !== "operational") continue;
    if (loc?.disposition === EXIT_DISPOSITION) continue;

    const event = deriveComingOnlineEvent(loc, today);
    if (!event || event.onlineDate > horizon) continue;
    events.push({
      officeId: event.officeId,
      officeName: event.officeName,
      delta: event.delta,
      effectiveDate: event.onlineDate,
    });
  }
  events.sort((a, b) => a.effectiveDate - b.effectiveDate);
  return events;
}

// SPACE-PIPELINE-NET-S1 / PULSE-COMING-ONLINE-INCLUDE-CAPACITY-SCHEDULE:
// one row per market that has at least one build event OR a positive net
// schedule-increase event within the market's own pipeline horizon — nets all
// additions against same-market exits/closures within the same window.
//
// The headline `metric` (net) is the sum of the canonical named office events.
// This keeps the market total reconciled with the office additions and exits
// shown in the component breakdown, rather than allowing a later schedule row
// to silently change the headline.
function buildComingOnlineRows(markets, today) {
  const rows = [];
  for (const market of (markets ?? [])) {
    const locations = market.locations ?? [];
    const horizon = marketPipelineHorizon(locations, today);
    if (horizon == null) continue;

    const buildEvents = locations
      .filter((loc) => BUILD_STAGES.has(loc?.stage))
      .map((loc) => deriveComingOnlineEvent(loc, today))
      .filter((event) => event && event.onlineDate <= horizon)
      .map((event) => ({
        officeId: event.officeId,
        officeName: event.officeName,
        seatsAdded: event.seatsAdded,
        onlineDate: event.onlineDate,
      }))
      .sort((a, b) => a.onlineDate - b.onlineDate);
    const scheduleIncreaseEvents = collectScheduleIncreaseEvents(locations, today, horizon);

    if (buildEvents.length === 0 && scheduleIncreaseEvents.length === 0) continue;

    const exitEvents = collectExitEvents(locations, today, horizon);

    const components = [
      ...buildEvents.map((e) => ({ officeId: e.officeId, officeName: e.officeName, delta: e.seatsAdded, onlineDate: e.onlineDate ?? null, closeDate: null })),
      // Schedule increases use effectiveDate as their onlineDate in the component shape
      // (positive delta, no closeDate — mirrors build event convention).
      ...scheduleIncreaseEvents.map((e) => ({ officeId: e.officeId, officeName: e.officeName, delta: e.delta, onlineDate: e.effectiveDate, closeDate: null })),
      ...exitEvents.map((e) => ({ officeId: e.officeId, officeName: e.officeName, delta: -e.seatsRemoved, onlineDate: null, closeDate: e.closeDate ?? null })),
    ];
    const net = components.reduce((sum, component) => sum + component.delta, 0);
    const hasOffset = exitEvents.length > 0;

    const additionEvents = [...buildEvents, ...scheduleIncreaseEvents];

    let status;
    if (hasOffset) {
      status = `${components.map((c) => `${formatSignedSeats(c.delta)} ${c.officeName}`).join(", ")} = ${formatSignedSeats(net)} net`;
    } else if (additionEvents.length === 1) {
      if (buildEvents.length === 1) {
        status = onlineDateLabel(buildEvents[0].onlineDate);
      } else {
        status = onlineDateLabel(scheduleIncreaseEvents[0].effectiveDate);
      }
    } else {
      status = components.map((c) => `${formatSignedSeats(c.delta)} ${c.officeName}`).join(", ");
    }

    const hubOfficeIds = new Set(
      locations.filter((l) => l.isHub === true).map((l) => l.locationId ?? l.id)
    );
    const isHub = components.some((c) => hubOfficeIds.has(c.officeId));

    // SPACE-PIPELINE-PANE-REFINE-2: primaryLocationId is the first build
    // component's officeId, falling back to the first schedule-increase
    // office — used by per-row deep-link navigation to target a real
    // location entry in MarketPlanPage rather than a market ID.
    const primaryLocationId = buildEvents[0]?.officeId ?? scheduleIncreaseEvents[0]?.officeId ?? null;
    const primaryLoc = primaryLocationId
      ? locations.find((l) => (l.locationId ?? l.id) === primaryLocationId) ?? null
      : null;

    rows.push({
      key:               market.id ?? market.name,
      primaryLocationId,
      marketName:        market.name,
      // expand-panel detail fields sourced from the primary build location
      officeName:        primaryLoc ? (primaryLoc.locationAlias ?? primaryLoc.locationId ?? primaryLoc.id ?? null) : null,
      stage:             primaryLoc?.stage ?? null,
      utilPct:           null,
      seatGap:           null,
      planNote:          primaryLoc?.planNote ?? null,
      leaseExpiry:       primaryLoc?.leaseExpiry ?? null,
      isHub,
      metric:            net,
      status,
      components,
      hasOffset,
      sortKey:           horizon.getTime(),
    });
  }
  return rows;
}

// Live-state descriptor derived from current utilization — NOT a fabricated
// historical trend (no time-series data is available from GET /api/plan).
function utilBandLabel(utilPct) {
  if (utilPct == null) return "No utilization data";
  if (utilPct < 60) return "Well under capacity";
  if (utilPct < 85) return "Comfortable";
  return "Tightening";
}

function deficitReasonLabel(seatGap) {
  return seatGap < 0 ? "Over capacity" : "At capacity";
}

function utilPrefix(utilPct) {
  return utilPct == null ? "" : `${utilPct}% assigned · `;
}

function buildRow({ loc, market, metric, status, sortKey }) {
  return {
    key:         loc.locationId,
    marketName:  market.name,
    isHub:       loc.isHub === true,
    metric,
    status,
    sortKey,
    // Expand-panel detail fields
    officeName:  loc.locationAlias ?? loc.locationId,
    stage:       loc.stage ?? null,
    utilPct:     loc.utilPct ?? null,
    seatGap:     loc.seatGap ?? null,
    planNote:    loc.planNote ?? null,
    leaseExpiry: loc.leaseExpiry ?? null,
  };
}

// SPACE-PIPELINE-PANE-PUNCHLIST: "Seats available" rows carry two distinct
// figures instead of one conflated "seats available" number:
//   availableNow — installed seats not currently occupied (installed −
//                  occupied headcount), ready to use with no desk
//                  configuration/install needed. Clamped at 0 (an office
//                  can't have negative ready-to-use seats) and null when
//                  live headcount data isn't available for the office.
//   headroom     — additional seats possible up to max density (max
//                  density − installed) — this is the pre-existing
//                  loc.seatGap value, unchanged. Requires desk
//                  configuration/install before use.
function buildSeatsAvailableRow({ loc, market, headroom, status }) {
  const availableNow = (loc.installedCapacity != null && loc.headcount != null)
    ? Math.max(0, loc.installedCapacity - loc.headcount)
    : null;
  return {
    key:         loc.locationId,
    marketName:  market.name,
    isHub:       loc.isHub === true,
    availableNow,
    headroom,
    status,
    sortKey:     availableNow ?? -1,
    // Expand-panel detail fields
    officeName:  loc.locationAlias ?? loc.locationId,
    stage:       loc.stage ?? null,
    utilPct:     loc.utilPct ?? null,
    seatGap:     loc.seatGap ?? null,
    planNote:    loc.planNote ?? null,
    leaseExpiry: loc.leaseExpiry ?? null,
  };
}

function finalizeColumn(rows, { unit, subLabel, sortFn }) {
  const sorted = [...rows].sort(sortFn);
  const rollup = sorted.reduce((sum, r) => sum + r.metric, 0);
  const marketCount = new Set(sorted.map((r) => r.marketName)).size;
  return { rows: sorted, rollup, unit, marketCount, subLabel };
}

function finalizeSeatsAvailableColumn(rows, { subLabel, sortFn }) {
  const sorted = [...rows].sort(sortFn);
  const rollupAvailableNow = sorted.reduce((sum, r) => sum + (r.availableNow ?? 0), 0);
  // Guard null headroom — offices with no maxCapacity on file have headroom: null
  const rollupHeadroom = sorted.reduce((sum, r) => sum + (r.headroom ?? 0), 0);
  const marketCount = new Set(sorted.map((r) => r.marketName)).size;
  return { rows: sorted, rollupAvailableNow, rollupHeadroom, marketCount, subLabel };
}

/**
 * @param {Array} markets  markets array from GET /api/plan (getPlan()).
 * @returns {{comingOnline: object, seatsAvailable: object, needsSeats: object}}
 */
export function buildSpacePipelineColumns(markets, today = new Date()) {
  const seatsAvailableRows = [];
  const needsSeatsRows     = [];

  for (const market of markets ?? []) {
    for (const loc of market.locations ?? []) {
      if (BUILD_STAGES.has(loc.stage)) continue;
      if (loc.stage !== "operational") continue;

      // PULSE-SPACE-PIPELINE-DEFICIT-BY-OFFICE: people-based bucketing.
      // computePeopleDelta returns null when installedCapacity or headcount is
      // absent — exclude those offices from both columns (no data to classify).
      const peopleDelta = computePeopleDelta(loc.installedCapacity, loc.headcount);
      if (peopleDelta === null) continue;

      if (peopleDelta > 0) {
        // headcount > installedCapacity → deficit (needs seats).
        // metric = headcount − installedCapacity (positive, count of seats short).
        // sortKey negated so most-deficit offices sort first in ascending order.
        needsSeatsRows.push({
          ...buildRow({
            loc, market, metric: peopleDelta,
            status: `${utilPrefix(loc.needsSeatsAssignmentPct)}Over capacity`,
            sortKey: -peopleDelta,
          }),
          utilPct: loc.needsSeatsAssignmentPct ?? null,
          // Use the same density-headroom source as Seats available, but
          // deficit rows display unavailable/non-positive expansion as 0.
          expansion: loc.seatGap > 0 ? loc.seatGap : 0,
        });
      } else {
        // installedCapacity >= headcount → surplus (seats available).
        // headroom = seatGap (density headroom: max_density − installed_seats),
        // unchanged — still measures densification room, not people-vs-seats gap.
        seatsAvailableRows.push(buildSeatsAvailableRow({
          loc, market, headroom: loc.seatGap ?? null,
          status: `${utilPrefix(loc.utilPct)}${utilBandLabel(loc.utilPct)}`,
        }));
      }
    }
  }

  return {
    comingOnline: finalizeColumn(buildComingOnlineRows(markets, today), {
      unit: "seats",
      subLabel: "Net new supply, minus same-market exits",
      sortFn: (a, b) => a.sortKey - b.sortKey,
    }),
    seatsAvailable: finalizeSeatsAvailableColumn(seatsAvailableRows, {
      subLabel: "Open seats in operational offices",
      sortFn: (a, b) => b.sortKey - a.sortKey,
    }),
    needsSeats: finalizeColumn(needsSeatsRows, {
      unit: "gap",
      subLabel: "Operational offices over practical capacity",
      sortFn: (a, b) => a.sortKey - b.sortKey,
    }),
  };
}
